import log from 'loglevel';
import React, { FC, useCallback, useEffect } from 'react';
import { FITROOM_APP_ID, FITROOM_APP_KEY, FITROOM_URL } from '../constants';
import {
  FitroomGarmentUpdatedEvent,
  FitroomMessageIncoming,
  FitroomMessageOutgoing,
  FitroomMessageType,
  FitroomSessionRes,
  FitroomSessionResPayload,
} from './fitroomWidgetTypes';

let source: MessageEventSource | undefined;

/**
 * Send message from host.
 * @param message Message.
 */
export const sendMessage = (message: FitroomMessageOutgoing) => {
  try {
    source!.postMessage(message, { targetOrigin: FITROOM_URL });
    log.debug(`Sent message ${message.type}`);
  } catch (error) {
    log.error(error);
  }
};

/**
 * Send credentials to Fitroom.
 * @param event The credentials request message event.
 */
const handleCredentialsReq = (event: MessageEvent) => {
  source = event.source as MessageEventSource;
  sendMessage({
    type: FitroomMessageType.CREDENTIALS_RES,
    payload: { appKey: FITROOM_APP_KEY },
  });
};

/**
 * Fitroom widget.
 */
export type FitroomWidgetProps = {
  onGarmentUpdated?: (garmentId: string) => void;
  onLoaded?: () => void;
  onSessionResponse?: (session: FitroomSessionResPayload) => void;
};

export const FitroomWidget: FC<FitroomWidgetProps> = (props: FitroomWidgetProps) => {
  /**
   * Widget to host message handler.
   * @param event Message event.
   */
  const messageHandler = useCallback(
    (event: MessageEvent): void => {
      log.debug(`Received message ${event.data.type}`);

      if (event.origin !== FITROOM_URL) {
        return;
      }

      const message = event.data as FitroomMessageIncoming;

      switch (message.type) {
        case FitroomMessageType.CREDENTIALS_REQ: {
          handleCredentialsReq(event);
          break;
        }
        case FitroomMessageType.APP_LOADED_EVENT: {
          if (props.onLoaded) {
            props.onLoaded();
          }
          break;
        }
        case FitroomMessageType.GARMENT_UPDATED_EVENT: {
          if (props.onGarmentUpdated) {
            const m = message as FitroomGarmentUpdatedEvent;
            props.onGarmentUpdated(m.payload);
          }
          break;
        }
        case FitroomMessageType.SESSION_RES: {
          if (props.onSessionResponse) {
            const m = message as FitroomSessionRes;
            props.onSessionResponse(m.payload);
          }
          break;
        }
        default: {
          console.debug(`Unrecognized message type: ${message.type}`);
        }
      }
    },
    [props],
  );

  useEffect(() => {
    // Bind a message event listener to window object
    window.addEventListener('message', messageHandler);
    // remove listener when uncomount
    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [messageHandler]);

  return (
    <iframe
      src={`${FITROOM_URL}?site=${FITROOM_APP_ID}`}
      style={{
        border: 'none',
        margin: 0,
        padding: 0,
        display: 'block',
        width: '100%',
        height: '100%',
      }}
      id="fitroom-widget"
      name="fitroom-widget"
      title="fitroom-widget"
    />
  );
};
